import { z } from 'zod';

/**
 * WHAT THE COMMISSIONER SETS, AND WHAT NOBODY ELSE GETS TO DECIDE.
 *
 * Everything in here is a business number. Not one of them has a right answer an engineer could
 * work out, which is why every single field is nullable and defaults to null rather than to
 * something plausible. A null target is "she has not said yet" and renders as a bracket; a
 * plausible default is a number the dashboard would quietly start holding people to.
 *
 * The bounds do a second job: they are what stops a director configuring a league the platform
 * cannot actually run. A cap above what a room holds, or a league length HQ does not print packs
 * for, is a problem discovered on the session. Set here, checked when she saves a league.
 */

/** A share of something, between none of it and all of it. */
const share = z.number().min(0).max(1).nullable();
const count = z.number().int().min(0).nullable();

export const platformSettingsSchema = z.object({
  /**
   * THE SIX YEAR-ONE TARGETS. These are the only thing that can turn a dashboard tile rust, and a
   * tile with no target here never does — see `verdictFor`.
   */
  targets: z
    .object({
      activeLeagues: count.default(null),
      fillRate: share.default(null),
      playersOnTheBooks: count.default(null),
      returnRate: share.default(null),
      sameDayScoreEntry: share.default(null),
      kitOnTime: share.default(null),
    })
    .default({}),

  /** The walls a director configures inside. */
  bounds: z
    .object({
      /** 28 is the platform's maximum, but a room may hold fewer and a market may want fewer. */
      maxPlayerCap: z.number().int().min(4).max(28).nullable().default(28),
      minPlayerCap: z.number().int().min(4).max(28).nullable().default(8),
      /** Days before session one that the roster stops moving. Ten, unless she says otherwise. */
      rosterLockDays: z.number().int().min(0).max(60).nullable().default(10),

      /**
       * HOW MANY FOUNDING PLACES THERE ARE. Twenty-five, per `docs/PRICING.md`.
       *
       * Non-null like the other bounds, and unlike the targets: this describes what the platform
       * does rather than a number nobody has chosen. It is here rather than as a literal because
       * the commissioner will want to raise it, and because that must not be a deploy.
       *
       * It caps FOUNDING STATUS, never directors. Past it the founding claim falls through and the
       * director joins on the standard plan — a full cohort is a normal state, not an error.
       */
      foundingDirectorCap: z.number().int().min(0).max(1000).nullable().default(25),

      /**
       * HOW LONG A PLAYER PROMOTED FROM THE WAITLIST HAS TO PAY for her seat — owner, 2026-10-06:
       * "configurable, 24 hours for now", and the commissioner's to set for every league. Past it
       * the seat goes back to the director to offer to the next person. Nobody pays to wait.
       */
      waitlistPaymentHours: z.number().int().min(1).max(168).nullable().default(24),
    })
    .default({}),
});

export type PlatformSettings = z.infer<typeof platformSettingsSchema>;

/** Everything null, everything unstated. What a fresh platform believes. */
export const DEFAULT_SETTINGS: PlatformSettings = platformSettingsSchema.parse({});

/**
 * HOW MANY SESSIONS A LEAGUE MAY HAVE: any whole number from one to twenty-four, as often as she
 * schedules them — two or three in a week is hers to choose, and nothing assumes weekly (owner,
 * 2026-10-19). This replaced the three fixed Formats — Short, Standard and Full, three,
 * six and eight sessions — and with them the `league_format` column: a league's length is now its
 * session rows and nothing else, so there is no second fact to disagree with them.
 *
 * Here beside the bounds because both front ends and the API read it.
 */
export const LEAGUE_SESSIONS = { min: 1, max: 24 } as const;

/**
 * THE FINAL SESSION: the highest-numbered session that is not voided. What the final-results email,
 * "get ready for your final session" and close-out all mean by "the last one". Null for a league
 * with no session left standing.
 */
export function finalSessionNumber(sessions: readonly { sessionNumber: number; status: string }[]): number | null {
  let last: number | null = null;
  for (const s of sessions) if (s.status !== 'voided' && (last === null || s.sessionNumber > last)) last = s.sessionNumber;
  return last;
}
